import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Card, CardActionArea, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import { useOptionalChatPanel } from '../../contexts/ChatPanelContext';
import useElementWidth from '../agent/dashboard/useElementWidth';
import { fill, useChatCardCopy, useChatCardTokens } from './chatCards';

// The kinds services/agents/ui_tools.py::show_artifact accepts.
const KIND_ICONS = {
  cma_report: AssessmentOutlinedIcon,
  marketing_report: CampaignOutlinedIcon,
  marketing_material: DescriptionOutlinedIcon,
  media: PhotoLibraryOutlinedIcon,
  // One listing's items in the approval queue (/agent/approvals?property=<id>), never one item.
  approvals: TaskAltRoundedIcon,
  listing: HomeOutlinedIcon,
};

// A row card's picture runs down its left edge, the card's full height, with no frame.
const MEDIA_WIDTH = 88;
const MEDIA_MIN_HEIGHT = 84;
// Several cards sit in one row that scrolls sideways once they no longer fit. In the docked
// panel, on a smaller screen or in a row narrower than WIDE_ROW_WIDTH they are compact: narrow
// tiles with the preview on top. On the full chat page of a wide screen they are wider row
// cards, the thumbnail beside the text.
const WIDE_ROW_WIDTH = 480;
const TILE_MIN = 196;
const TILE_MAX = 240;
const ROW_CARD_MIN = 300;
const ROW_CARD_MAX = 360;
const NO_EDGES = { prev: false, next: false };

/** An in-app path the router can open ("/agent/approvals"), never another site. */
const isAppRoute = (route) => typeof route === 'string' && route.startsWith('/') && !route.startsWith('//');

const clampSx = (lines) => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});

const ArtifactPreview = ({ artifact, tokens, iconSize, sx }) => {
  const [failed, setFailed] = useState(false);
  const Icon = KIND_ICONS[artifact.kind] || DescriptionOutlinedIcon;
  const preview = artifact.preview_url && !failed ? artifact.preview_url : null;

  return (
    <Box
      aria-hidden="true"
      sx={{
        position: 'relative',
        flexShrink: 0,
        overflow: 'hidden',
        display: 'grid',
        placeItems: 'center',
        color: tokens.primary,
        backgroundColor: tokens.soft,
        ...sx,
      }}
    >
      {preview ? (
        <Box
          component="img"
          src={preview}
          alt=""
          loading="lazy"
          onError={() => setFailed(true)}
          sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Icon sx={{ fontSize: iconSize }} />
      )}
    </Box>
  );
};

/**
 * One card. `single` is the lone card under an answer (a row, text in full); `row` and `tile`
 * sit side by side in the scrolling row, filling its height, their text clamped so one long
 * subtitle doesn't stretch the whole row.
 */
const ArtifactCard = ({ artifact, copy, tokens, onOpen, variant = 'single' }) => {
  const tile = variant === 'tile';
  const inRow = variant !== 'single';
  const kindLabel = copy?.artifactKinds?.[artifact.kind] || '';
  const lines = (count) => (inRow ? clampSx(count) : null);

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2.5,
        borderColor: tokens.line,
        boxShadow: 'none',
        transition: `border-color ${tokens.motion}`,
        '&:hover': { transform: 'none', boxShadow: 'none', borderColor: tokens.lineHover },
        ...(inRow && { height: '100%' }),
        ...tokens.enter,
      }}
    >
      <CardActionArea
        component={RouterLink}
        to={artifact.route}
        onClick={onOpen}
        sx={{
          display: 'flex',
          flexDirection: tile ? 'column' : 'row',
          alignItems: 'stretch',
          justifyContent: 'flex-start',
          ...(inRow && { height: '100%' }),
          '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
          '&.Mui-focusVisible': { outline: `2px solid ${tokens.primary}`, outlineOffset: -2 },
        }}
      >
        <ArtifactPreview
          artifact={artifact}
          tokens={tokens}
          iconSize={tile ? 32 : 26}
          sx={tile
            ? { width: '100%', aspectRatio: '16 / 10' }
            : { width: MEDIA_WIDTH, minHeight: MEDIA_MIN_HEIGHT }}
        />
        <Box
          sx={{
            minWidth: 0,
            flex: 1,
            ...(tile ? { p: 1.25 } : { alignSelf: 'center', py: 1.25, pl: 1.5, pr: 0.5 }),
          }}
        >
          {kindLabel && (
            <Typography variant="caption" component="span" sx={{ display: 'block', color: tokens.slate[600] }}>
              {kindLabel}
            </Typography>
          )}
          <Typography
            variant="subtitle2"
            component="span"
            sx={{ display: 'block', color: tokens.slate[900], overflowWrap: 'anywhere', ...lines(2) }}
          >
            {artifact.title}
          </Typography>
          {artifact.subtitle && (
            <Typography
              variant="body2"
              component="span"
              sx={{ display: 'block', color: tokens.slate[600], ...lines(2) }}
            >
              {artifact.subtitle}
            </Typography>
          )}
        </Box>
        {!tile && (
          <ChevronRightRoundedIcon
            aria-hidden="true"
            sx={{ color: tokens.slate[500], flexShrink: 0, alignSelf: 'center', mr: 0.75 }}
          />
        )}
      </CardActionArea>
    </Card>
  );
};

/**
 * Cards under a chat answer for what the app made or holds (show_artifact): a CMA report, a
 * marketing plan or piece, photos or a video, an item waiting for approval, a listing. Each
 * shows its kind, title, subtitle and an optional preview image, and opens its page (`route`).
 * One card is a full-width row; several sit side by side in one row that scrolls sideways,
 * with previous / next buttons once they overflow. `compact` (the docked panel) keeps them
 * compact whatever the width; see WIDE_ROW_WIDTH for the rest.
 * On a phone the docked chat covers the page, so opening a card tucks the chat away.
 */
const ArtifactCards = ({ artifacts, compact = false }) => {
  const copy = useChatCardCopy();
  const tokens = useChatCardTokens();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const chat = useOptionalChatPanel();
  const [measureRef, width] = useElementWidth();
  const scrollerRef = useRef(null);
  const [edges, setEdges] = useState(NO_EDGES);

  const items = (Array.isArray(artifacts) ? artifacts : []).filter(
    (item) => item && item.title && isAppRoute(item.route),
  );
  const tiles = compact || isSmallScreen || width < WIDE_ROW_WIDTH;

  const updateEdges = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const end = el.scrollWidth - el.clientWidth;
    const prev = el.scrollLeft > 1;
    const next = el.scrollLeft < end - 1;
    setEdges((current) => (current.prev === prev && current.next === next ? current : { prev, next }));
  }, []);

  useLayoutEffect(() => {
    updateEdges();
  }, [updateEdges, width, tiles, items.length]);

  if (!items.length) return null;

  const handleOpen = () => {
    const panel = chat?.chatPanelState;
    if (isPhone && panel?.isVisible && !panel.isMinimized) chat.toggleMinimizeChat();
  };

  if (items.length === 1) {
    const [artifact] = items;
    return (
      <Stack component="ul" spacing={1} sx={{ listStyle: 'none', m: 0, mt: 1.5, p: 0 }}>
        <Box component="li">
          <ArtifactCard artifact={artifact} copy={copy} tokens={tokens} onOpen={handleOpen} />
        </Box>
      </Stack>
    );
  }

  const scrollPage = (direction) => {
    const el = scrollerRef.current;
    if (!el) return;
    const left = direction * el.clientWidth * 0.85;
    if (typeof el.scrollBy === 'function') el.scrollBy({ left, behavior: reduceMotion ? 'auto' : 'smooth' });
    else el.scrollLeft += left;
  };

  const rowCopy = copy?.artifactRow || {};
  const overflowing = edges.prev || edges.next;
  const arrow = (direction, label, Icon, enabled) => (
    <Tooltip title={label}>
      <span>
        <IconButton aria-label={label} disabled={!enabled} onClick={() => scrollPage(direction)}>
          <Icon />
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box ref={measureRef} sx={{ mt: 1.5, minWidth: 0 }}>
      {overflowing && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
          <Typography variant="caption" sx={{ color: tokens.slate[600] }}>
            {fill(rowCopy.count, { count: items.length })}
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {arrow(-1, rowCopy.previous, ChevronLeftRoundedIcon, edges.prev)}
            {arrow(1, rowCopy.next, ChevronRightRoundedIcon, edges.next)}
          </Box>
        </Box>
      )}
      <Box
        component="ul"
        ref={scrollerRef}
        onScroll={updateEdges}
        sx={{
          listStyle: 'none',
          m: 0,
          p: 0,
          // Room for the scrollbar and the cards' 6px entry rise, so neither clips.
          pb: 1,
          display: 'grid',
          gridAutoFlow: 'column',
          gridAutoColumns: tiles
            ? `minmax(${TILE_MIN}px, ${TILE_MAX}px)`
            : `minmax(${ROW_CARD_MIN}px, ${ROW_CARD_MAX}px)`,
          gap: 1.5,
          overflowX: 'auto',
          overscrollBehaviorX: 'contain',
          scrollSnapType: 'x mandatory',
          scrollbarWidth: 'thin',
          '& > li': { minWidth: 0, scrollSnapAlign: 'start' },
        }}
      >
        {items.map((artifact) => (
          <Box component="li" key={`${artifact.kind}-${artifact.id}`}>
            <ArtifactCard
              artifact={artifact}
              copy={copy}
              tokens={tokens}
              onOpen={handleOpen}
              variant={tiles ? 'tile' : 'row'}
            />
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ArtifactCards;
